// ================================================================
// WIRE PROTOCOL
// ================================================================
// Mirrors the two wire formats the client speaks:
//   1. Matchmaking API  — API Gateway WebSocket, backend/handler/*.py
//   2. Game server      — direct WebSocket to the C++ binary, server-cpp/main.cpp
// Keep these in sync with those two sources; they are the contract.

// ── 1. Matchmaking API ──────────────────────────────────────────

/** Client → API Gateway. */
export type MatchmakingRequest =
  | { action: 'startMatchmaking'; playerId: string }
  | { action: 'stopMatchmaking'; playerId: string }
  | { action: 'acceptMatch'; playerId: string };

/** Details needed to open a socket to the game server, from `matchReady`. */
export interface MatchDetails {
  /** UUID hostname resolved from the instance IP; preferred over `ip` (TLS). */
  dns?: string;
  /** Raw EC2 public IP — the dev Anywhere fleet has no DNS/TLS. */
  ip?: string;
  /** External port already reported by ProcessReady(); never offset it. */
  port: number;
  /** GameLift player session id, sent to the game server as the handshake. */
  psess: string;
}

/** API Gateway → client, pushed by the Lambda handlers. */
export type MatchmakingMessage =
  | { action: 'matchFound'; ticketId?: string }
  | ({ action: 'matchReady' } & MatchDetails)
  | { action: 'matchFailed'; reason?: string };

/**
 * `matchFailed` reasons that mean "the queue moved on", not "something broke".
 * The client silently re-enters the queue for these.
 */
export const TRANSIENT_MATCH_FAILURES = new Set([
  'MatchmakingTimedOut',
  'MatchmakingCancelled',
  'MatchExpired',
]);

// ── 2. Game server ──────────────────────────────────────────────

export type Move = 'rock' | 'paper' | 'scissors';
/** What a player actually put on the board — `timeout` if they never moved. */
export type PlayedMove = Move | 'timeout';
export type RoundOutcome = 'WIN' | 'LOSS' | 'TIE' | 'TIMEOUT_LOSS';

export interface Scores {
  you: number;
  opponent: number;
}

/** Client → game server. */
export type GameRequest =
  | { psid: string; playerName: string } // handshake, first message on the socket
  | { move: Move };

/** Game server → client. */
export type GameMessage =
  | { type: 'WELCOME'; index: number }
  | { type: 'MATCH_INFO'; your_name: string; opponent_name: string }
  | { type: 'PROMPT'; msg: string; scores: Scores; time_limit_seconds: number }
  | {
      type: 'ROUND_RESULT';
      result: RoundOutcome;
      you_played: PlayedMove;
      opponent_played: PlayedMove;
      scores: Scores;
    }
  | { type: 'OPPONENT_DISCONNECTED'; grace_period_seconds: number }
  | { type: 'OPPONENT_RECONNECTED' }
  | {
      type: 'GAME_OVER';
      i_won: boolean;
      reason?: 'OPPONENT_ABANDONED';
      winner_name: string;
      final_scores: Scores;
    }
  | { type: 'ERROR'; msg: string };

export const MOVES: readonly Move[] = ['rock', 'paper', 'scissors'];
